<template>
  <div class="max-w-6xl mx-auto px-4 py-12 relative">
    <!-- Screen Loading Lock overlay -->
    <div v-if="loading" class="absolute inset-0 z-40 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center rounded-3xl">
      <div class="flex flex-col items-center">
        <svg class="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="mt-4 text-indigo-600 dark:text-indigo-400 font-semibold text-lg">Processing...</p>
      </div>
    </div>

    <div
      v-if="!canManageBilling"
      class="mx-auto max-w-xl rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
      data-testid="billing-owner-managed"
    >
      <div class="flex gap-4">
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full"
          :class="hasActiveSubscription ? 'bg-indigo-100 dark:bg-indigo-500/10' : 'bg-amber-100 dark:bg-amber-500/10'"
        >
          <BuildingOffice2Icon
            class="size-6"
            :class="hasActiveSubscription ? 'text-indigo-600 dark:text-indigo-400' : 'text-amber-600 dark:text-amber-400'"
            aria-hidden="true"
          />
        </div>
        <div>
          <template v-if="hasActiveSubscription">
            <h1 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ organizationName }} is on the {{ formatEnum(subscription?.flavor) }} plan
            </h1>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Billing is managed by the organization owner. Your access comes from their plan.
            </p>
          </template>
          <template v-else>
            <h1 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ organizationName }} has no active plan
            </h1>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Runners of this organization are available once it has a plan. Ask the organization
              owner to choose one, or switch to another organization with the menu at the top.
            </p>
          </template>
          <RouterLink
            :to="{ name: 'OrganizationSettings' }"
            class="mt-4 inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            >Organization settings</RouterLink
          >
        </div>
      </div>
    </div>

    <template v-else>
    <div class="text-center mb-12">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
        Pricing Plans
      </h1>
      <p class="mt-4 text-xl text-gray-600 dark:text-gray-400">
        Choose the plan that's right for your team.
      </p>
    </div>

    <!-- Current subscription banner (only for active users) -->
    <div
      v-if="hasActiveSubscription"
      class="mb-8 p-4 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-between flex-wrap gap-4"
    >
      <div class="flex items-center gap-4">
        <div class="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center shrink-0">
          <svg class="h-4 w-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-gray-900 dark:text-white">
            You're currently on the <strong>{{ subscription?.flavor }}</strong> plan.
          </p>
          <p class="text-xs text-gray-700 dark:text-gray-300 mt-1">
            Your subscription is currently <strong>{{ formatEnum(subscription?.status) }}</strong>. Please navigate to Profile/Billing to manage your subscription.
          </p>
        </div>
      </div>
      <router-link
        to="/profile"
        class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      >
        Go to Profile
      </router-link>
      <!-- Pending Downgrade Banner -->
      <div
        v-if="subscription?.pendingFlavor"
        class="mt-4 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20"
      >
        <p class="text-sm text-blue-800 dark:text-blue-300">
          Your plan will be downgraded to <strong>{{ subscription.pendingFlavor }}</strong> at the end of the billing cycle. You'll retain access to your current features until then.
          <br />
          If you wish to keep your current plan, please contact our support team.
        </p>
      </div>

    </div>

    <!-- PENDING banner -->
    <div
      v-if="subscription && subscription.status === 'PENDING'"
      class="mb-8 p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20"
    >
      <div class="flex items-center gap-3">
        <svg class="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        <p class="text-sm font-medium text-amber-700 dark:text-amber-300">
          Your last checkout was incomplete. Choose a plan below to start a new checkout.
        </p>
      </div>
    </div>

    <section
      v-if="verificationRequired"
      class="mb-10 rounded-2xl border border-indigo-200 bg-indigo-50 p-6 dark:border-indigo-500/30 dark:bg-indigo-500/10"
      aria-labelledby="verify-email-heading"
      data-testid="verify-email-panel"
    >
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-500/20"
        >
          <EnvelopeIcon class="size-5 text-indigo-600 dark:text-indigo-300" aria-hidden="true" />
        </div>
        <div class="min-w-0 flex-1 space-y-3">
          <h2 id="verify-email-heading" class="text-lg font-semibold text-gray-900 dark:text-white">
            Verify your email to activate a plan
          </h2>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            We sent a 6-digit code to
            <strong class="break-all">{{ auth.username ?? 'your email address' }}</strong>. Enter it
            below.
          </p>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Bought on our website? Your plan activates once you verify.
          </p>
          <EmailVerificationForm />
        </div>
      </div>
    </section>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <!-- Plan Card (repeated for each plan) -->
      <div
        v-for="plan in plans"
        :key="plan.flavor"
        :class="[
          'flex flex-col p-8 rounded-3xl transition-all duration-300 relative group',
          isCurrentPlan(plan.flavor)
            ? 'bg-white dark:bg-gray-900 border-2 border-indigo-600 shadow-md ring-2 ring-indigo-600/20 z-10'
            : plan.highlighted
              ? 'bg-gradient-to-b from-indigo-50/50 to-white dark:from-gray-800/80 dark:to-gray-900 border-2 border-indigo-500 shadow-2xl hover:-translate-y-1'
              : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-xl hover:-translate-y-1 dark:hover:border-gray-700 border-b-4',
        ]"
      >
        <!-- Background decorative blur for highlighted plan -->
        <div v-if="plan.highlighted && !isCurrentPlan(plan.flavor)" class="absolute inset-0 -z-10 rounded-3xl bg-indigo-500/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
        
        <!-- Popular badge -->
        <div v-if="plan.highlighted && !isCurrentPlan(plan.flavor)" class="absolute -top-4 inset-x-0 flex justify-center">
          <span class="inline-flex items-center px-4 py-1 rounded-full text-xs font-bold tracking-widest bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow uppercase">
            Most Popular
          </span>
        </div>

        <div class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ plan.name }}</h2>
            <span
              v-if="isCurrentPlan(plan.flavor)"
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300 ring-1 ring-inset ring-indigo-500/20"
            >
              Current Plan
            </span>
          </div>
          <p class="text-gray-500 dark:text-gray-400 text-sm h-10">{{ plan.description }}</p>
        </div>

        <div class="mb-8">
          <div class="flex items-baseline text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            {{ plan.price }}
            <span class="ml-1 text-lg font-medium text-gray-500 dark:text-gray-400">{{ plan.period }}</span>
          </div>
          <p v-if="plan.currency === 'EUR'" class="mt-1 text-sm text-gray-500 dark:text-gray-400">excl. VAT</p>
        </div>

        <ul role="list" class="space-y-4 mb-8 flex-1">
          <li v-for="feature in plan.features" :key="feature" class="flex items-start gap-3">
            <svg class="h-6 w-5 flex-none text-indigo-500 dark:text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span class="text-gray-600 dark:text-gray-300 text-sm mt-0.5">{{ feature }}</span>
          </li>
        </ul>
        
        <div v-if="!hasActiveSubscription" class="mt-auto">
          <p
            v-if="verificationRequired"
            class="w-full rounded-xl border border-dashed border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-600 dark:border-white/15 dark:text-gray-400"
          >
            Verify your email to activate a plan
          </p>
          <button
            v-else
            @click="handleCheckout(plan.flavor)"
            :disabled="loading"
            :class="[
              'w-full py-3 px-4 font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-wait shadow-sm cursor-pointer',
              plan.highlighted
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20 hover:shadow-lg hover:shadow-indigo-600/30'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20 hover:shadow-lg hover:shadow-indigo-600/30',
            ]"
          >
            {{ loading ? 'Processing...' : 'Choose ' + plan.name }}
          </button>
          <a v-if="plan.flavor === 'ENTERPRISE'" href="mailto:sales@managerunners.com" :class="salesLinkClass">
            Questions? Contact sales
          </a>
        </div>
        
        <div v-else-if="!isCurrentPlan(plan.flavor) && !subscription?.pendingFlavor" class="mt-auto flex flex-col items-center">
          <button
            v-if="isUpgrade(plan.flavor) && canSwitchPlan"
            @click="promptSwitchPlan(plan.flavor)"
            :disabled="loading"
            class="w-full py-3 px-4 font-semibold rounded-xl transition-all duration-200 text-white bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20 shadow-sm hover:shadow-lg hover:shadow-indigo-600/30 disabled:opacity-50 cursor-pointer disabled:cursor-wait"
          >
            Upgrade to {{ plan.name }}
          </button>
          <a v-if="plan.flavor === 'ENTERPRISE'" href="mailto:sales@managerunners.com" :class="salesLinkClass">
            Questions? Contact sales
          </a>
        </div>
      </div>
    </div>

    <!-- Error banner -->
    <div v-if="error" class="mt-8 p-4 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-center border border-red-200 dark:border-red-800">
      {{ error }}
    </div>

    </template>

    <!-- Confirm Plan Switch Modal -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-gray-100 dark:border-gray-700">
        <div class="mb-6">
          <div class="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400">
            <svg v-if="confirmIsUpgrade" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <svg v-else class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Confirm {{ confirmIsUpgrade ? 'Upgrade' : 'Downgrade' }}
          </h3>
          <p v-if="confirmIsUpgrade" class="text-gray-600 dark:text-gray-300">
            You are upgrading to <strong>{{ confirmTargetFlavor }}</strong>.
            You will be charged a prorated amount immediately for the difference in cost, and gain instant access to new features.
          </p>
          <p v-else class="text-gray-600 dark:text-gray-300">
            You are downgrading to <strong>{{ confirmTargetFlavor }}</strong>.
            <br/><br/>
            You will lose access to certain features. Your existing runners will continue to run.
            Your existing plan access will continue until the end of your billing cycle.
          </p>

          <div v-if="subscription?.cancelAtNextBillingDate" class="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-lg text-sm border border-indigo-100 dark:border-indigo-800">
            <strong>Note:</strong> Your scheduled cancellation will be revoked and your subscription will be reactivated automatically!
          </div>
        </div>
        
        <div class="flex gap-4">
          <button
            @click="showConfirmModal = false"
            class="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            @click="executeSwitchPlan"
            :disabled="loading"
            class="flex-1 py-2.5 px-4 font-semibold text-white rounded-xl transition-all shadow-sm"
            :class="[
              confirmIsUpgrade 
                ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20' 
                : 'bg-red-600 hover:bg-red-700 shadow-red-600/20'
            ]"
          >
            {{ loading ? 'Processing...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {EnvelopeIcon} from '@heroicons/vue/24/outline';
import {type SubscriptionFlavor, useSubscriptionStore} from '@/stores/subscription';
import {useAuthStore} from '@/stores/auth';
import {useToastStore} from '@/stores/toast';
import {formatEnum} from '@/lib/format';
import {useOrganizationStore} from '@/stores/organization';
import {BuildingOffice2Icon} from '@heroicons/vue/24/outline';
import {isEmailNotVerified} from '@/lib/email-verification';
import EmailVerificationForm from '@/components/EmailVerificationForm.vue';

const FLAVOR_ORDER: Record<SubscriptionFlavor, number> = {
  BASIC: 0,
  PRO: 1,
  F_AND_F: 2,
  ENTERPRISE: 3,
};

const subscriptionStore = useSubscriptionStore();
const auth = useAuthStore();
const toast = useToastStore();
const organizations = useOrganizationStore();
const canManageBilling = computed(() => organizations.canManageBilling);
const organizationName = computed(() => organizations.current?.name ?? 'This organization');
const plans = computed(() => subscriptionStore.plans);

const loading = computed(() => subscriptionStore.loading);
const error = computed(() => subscriptionStore.error);
const subscription = computed(() => subscriptionStore.subscription);
const hasActiveSubscription = computed(() => subscription.value && ['ACTIVE', 'ON_HOLD'].includes(subscription.value.status));

// On-hold subscriptions must resolve their payment issue before switching; F&F plans are granted by an
// administrator, so their upgrades go through sales.
const canSwitchPlan = computed(
  () => subscription.value?.status === 'ACTIVE' && subscription.value.flavor !== 'F_AND_F'
);

const salesLinkClass =
  'mt-3 block text-center text-sm text-gray-500 underline underline-offset-2 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400';

const showConfirmModal = ref(false);
const confirmTargetFlavor = ref<SubscriptionFlavor | null>(null);
const confirmIsUpgrade = ref(false);

// Rendered only in the owner part of the page; members see the owner notice instead.
const verificationRequired = computed(
  () => !hasActiveSubscription.value && auth.emailVerified === false
);

onMounted(() => {
  subscriptionStore.fetchSubscription();
  if (canManageBilling.value) {
    subscriptionStore.fetchPlans();
    auth.fetchMe().catch(() => {
      // Without /me, a checkout's EMAIL_NOT_VERIFIED response still shows the panel.
    });
  }
});

function isCurrentPlan(flavor: SubscriptionFlavor): boolean {
  return subscription.value?.status === 'ACTIVE' && subscription.value?.flavor === flavor;
}

function isUpgrade(targetFlavor: SubscriptionFlavor): boolean {
  if (!subscription.value?.flavor) return true;
  return FLAVOR_ORDER[targetFlavor] > FLAVOR_ORDER[subscription.value.flavor];
}

function promptSwitchPlan(flavor: SubscriptionFlavor) {
  confirmTargetFlavor.value = flavor;
  confirmIsUpgrade.value = isUpgrade(flavor);
  showConfirmModal.value = true;
}

async function executeSwitchPlan() {
  if (!confirmTargetFlavor.value) return;
  try {
    const wasUpgrade = confirmIsUpgrade.value;
    const result = await subscriptionStore.switchPlan(confirmTargetFlavor.value);
    if (!result) return;
    showConfirmModal.value = false;
    
    if (wasUpgrade) {
      toast.show('Plan upgraded successfully! Enjoy new features.', 'success');
    } else {
      toast.show('Plan change scheduled for the end of your billing cycle.', 'success');
    }
  } catch {
    // Error is handled and displayed in the UI banner
  }
}

async function handleCheckout(flavor: SubscriptionFlavor) {
  try {
    await subscriptionStore.checkout(flavor);
  } catch (e) {
    if (isEmailNotVerified(e)) {
      subscriptionStore.error = null;
      auth.emailVerified = false;
    }
  }
}
</script>
